import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { roleLabel, hasRole, userRoles } from "@/lib/format";
import {
    LayoutDashboard, FileText, CheckSquare, ClipboardList, ScanLine,
    SlidersHorizontal, CreditCard, Building2, LogOut, Menu, X, ShieldCheck, Tag, Settings, UserCog, MailCheck, LifeBuoy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { VersionBadge } from "@/components/VersionBadge";
import { CookieNotice } from "@/components/CookieNotice";
import { openCookiePreferences } from "@/lib/consent";

const SCHOOL = ["school_admin", "purchasing_admin", "approver", "requester"];
const SCHOOL_NAV = [
    { to: "/app", end: true, label: "Dashboard", icon: LayoutDashboard, roles: SCHOOL },
    { to: "/app/requests", label: "Purchase Requests", icon: FileText, roles: SCHOOL },
    { to: "/app/approvals", label: "Approvals", icon: CheckSquare, roles: ["school_admin", "approver"] },
    { to: "/app/orders", label: "Purchase Orders", icon: ClipboardList, roles: SCHOOL },
    { to: "/app/invoices", label: "Invoices & OCR", icon: ScanLine, roles: ["school_admin", "purchasing_admin"] },
    { to: "/app/email-invoices", label: "Review Email Invoices", icon: MailCheck, roles: ["school_admin", "purchasing_admin"] },
    { to: "/app/rules", label: "Approval Rules", icon: SlidersHorizontal, roles: ["school_admin"] },
    { to: "/app/billing", label: "Billing", icon: CreditCard, roles: ["school_admin"] },
    { to: "/app/settings", label: "School Settings", icon: Settings, roles: ["school_admin", "purchasing_admin"] },
    { to: "/app/help", label: "Help & Support", icon: LifeBuoy, roles: SCHOOL },
];
const ADMIN_NAV = [
    { to: "/app/admin", end: true, label: "Platform Console", icon: Building2, roles: ["platform_admin"] },
    { to: "/app/admin/versions", label: "Versioning", icon: Tag, roles: ["platform_admin"] },
    { to: "/app/help", label: "Help & Support", icon: LifeBuoy, roles: ["platform_admin"] },
];

export default function Layout() {
    const { user, tenant, logout, stopImpersonate } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    useEffect(() => { setOpen(false); }, [navigate]);

    if (user === null) return <Navigate to="/login" replace />;
    if (!user) return null;

    const isSuper = hasRole(user, "platform_admin");
    const impersonating = !!user?.impersonated_by;
    const nav = (isSuper ? ADMIN_NAV : SCHOOL_NAV).filter((n) => hasRole(user, n.roles));

    const NavItems = () => (
        <nav className="flex flex-col gap-1 px-3">
            {nav.map((n) => {
                const Icon = n.icon;
                return (
                    <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                                isActive
                                    ? "bg-emerald-50 text-brand-emerald font-semibold border border-emerald-100"
                                    : "text-muted-foreground hover:text-brand-emerald hover:bg-secondary"
                            }`
                        }
                    >
                        <Icon className="h-[18px] w-[18px]" />
                        {n.label}
                    </NavLink>
                );
            })}
        </nav>
    );

    const Sidebar = () => (
        <div className="flex h-full flex-col bg-white border-r border-border">
            <div className="flex items-center gap-2 px-5 h-16 border-b border-border">
                <img src="/procureed-logo.png" alt="ProcureEd" className="h-8 w-auto" />
            </div>
            <div className="py-4 flex-1 overflow-y-auto pe-scroll">
                <NavItems />
            </div>
            <div className="p-4 border-t border-border text-xs text-muted-foreground space-y-2">
                <div className="flex items-center justify-between">
                    <span>{isSuper ? "Platform Operations" : "Powered by Veracross"}</span>
                    <VersionBadge className="text-[11px]" />
                </div>
                <button onClick={openCookiePreferences} className="hover:text-brand-emerald transition-colors" data-testid="app-cookie-prefs">Cookie preferences</button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-background">
            {/* Desktop sidebar */}
            <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 z-30">
                <Sidebar />
            </aside>

            {/* Mobile drawer */}
            {open && (
                <div className="lg:hidden fixed inset-0 z-40" data-testid="mobile-nav">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
                    <div className="absolute inset-y-0 left-0 w-64">
                        <Sidebar />
                    </div>
                </div>
            )}

            <div className="lg:pl-64">
                {impersonating && (
                    <div className="bg-amber-500 text-white text-sm px-4 sm:px-6 py-2 flex flex-wrap items-center justify-between gap-2" data-testid="impersonation-banner">
                        <span className="flex items-center gap-2"><UserCog className="h-4 w-4" /> Viewing as <b>{user.name}</b>{tenant?.name ? ` · ${tenant.name}` : ""} (impersonated)</span>
                        <Button size="sm" variant="secondary" className="h-7 rounded-full" data-testid="stop-impersonation"
                            onClick={async () => { await stopImpersonate(); navigate("/app/admin"); }}>Return to Platform Admin</Button>
                    </div>
                )}
                <header className="sticky top-0 z-20 h-16 bg-white/85 backdrop-blur-md border-b border-border flex items-center gap-3 px-4 sm:px-6">
                    <button className="lg:hidden text-brand-emerald" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                    <div className="flex items-center gap-2 min-w-0">
                        <Building2 className="h-4 w-4 text-brand-teal shrink-0" />
                        <span className="font-semibold text-sm truncate" data-testid="active-tenant-name">
                            {isSuper ? "ProcureEd Platform" : tenant?.name || "—"}
                        </span>
                    </div>
                    <div className="ml-auto flex items-center gap-3">
                        {!isSuper && (
                            <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="veracross-sync-badge">
                                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                                Veracross <span className="text-emerald-700 font-medium">connected</span>
                            </div>
                        )}
                        <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-secondary text-brand-emerald border border-border" data-testid="user-role-badge">
                            <ShieldCheck className="h-3.5 w-3.5" /> {roleLabel(userRoles(user)[0])}
                        </span>
                        <div className="text-right hidden sm:block">
                            <div className="text-xs font-semibold leading-tight" data-testid="user-name">{user.name}</div>
                            <div className="text-[11px] text-muted-foreground leading-tight">{user.email}</div>
                        </div>
                        <Button
                            variant="ghost" size="icon"
                            className="text-brand-emerald hover:bg-secondary"
                            data-testid="logout-button"
                            onClick={() => { logout(); navigate("/login"); }}
                        >
                            <LogOut className="h-[18px] w-[18px]" />
                        </Button>
                    </div>
                </header>

                <main className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
                    <Outlet />
                </main>
            </div>
            <CookieNotice />
        </div>
    );
}
